import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, FileText, Calendar, DollarSign, Clock, LogOut,
  Users, Briefcase, UserSquare2, TrendingUp, Megaphone, Shield
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import Logo from '../../components/Logo';

const EmployeeLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!user || !['employee', 'hr', 'admin'].includes(user.role)) {
      navigate('/employee/login');
    }
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/employee/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  ];

  // Employee-only items
  if (user && user.role === 'employee') {
    navItems.push(
      { path: '/employee/checkin', icon: Clock, label: 'Check In/Out' },
      { path: '/employee/leaves', icon: Calendar, label: 'My Leaves' },
      { path: '/employee/payslips', icon: DollarSign, label: 'Payslips' },
      { path: '/employee/attendance', icon: FileText, label: 'My Attendance' }
    );
  }

  // HR-specific items
  if (user && (user.role === 'hr' || user.role === 'admin')) {
    navItems.push(
      { path: '/employee/hr/employees', icon: Users, label: 'Employees' },
      { path: '/employee/hr/leaves', icon: Calendar, label: 'Leave Approvals' },
      { path: '/employee/hr/attendance', icon: Clock, label: 'Attendance' },
      { path: '/employee/hr/payroll', icon: DollarSign, label: 'Payroll' },
      { path: '/employee/hr/jobs', icon: Briefcase, label: 'Job Listings' },
      { path: '/employee/hr/applications', icon: UserSquare2, label: 'Job Applications' },
      { path: '/employee/hr/announcements', icon: Megaphone, label: 'Announcements' },
      { path: '/employee/hr/performance', icon: TrendingUp, label: 'Performance' },
      { path: '/employee/hr/erp-access', icon: Shield, label: 'ERP Access' }
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-64 bg-gradient-to-b from-[#214979] to-[#214979]/90 text-white flex flex-col">
        <div className="p-6 border-b border-white/10">
          <Logo inverted={true} variant="icon" />
          <p className="text-xs text-[#c0932a]/80 mt-3">
            {user.role === 'hr' ? 'HR Portal' : user.role === 'admin' ? 'Admin Portal' : 'Employee Portal'}
          </p>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-secondary text-primary font-semibold'
                    : 'text-white/80 hover:bg-white/10'
                }`}
              >
                <Icon size={20} />
                <span className="text-sm">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="mb-3 px-4">
            <p className="text-sm font-medium">{user.full_name}</p>
            <p className="text-xs text-white/60">{user.role.toUpperCase()}</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-white hover:bg-white/10"
          >
            <LogOut size={20} className="mr-3" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64 min-h-screen">
        <header className="bg-white border-b border-gray-200 px-8 py-4">
          <h1 className="text-2xl font-bold text-gray-900">
            {navItems.find(item => item.path === location.pathname)?.label || 'Employee Portal'}
          </h1>
        </header>
        
        <main className="p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default EmployeeLayout;
